/**
 * Selected Racking Walk Slice
 * Redux slice for managing the selected racking inspection walk data
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the types for the racking inspection data structure
interface SignData {
  Status: boolean;
  Signature: string | null;
}

interface MiscData {
  Status: boolean;
  IMG1: string | null;
  IMG2: string | null;
  DESC1: string | null;
  DESC2: string | null;
  IsIMG1: boolean;
  IsIMG2: boolean;
}

interface LCItem {
  Status: boolean;
  QNUM: number;
  COMMENTS: string;
  INSTANCES: number;
  IMG1: string | null;
  IMG2: string | null;
  DESC1: string | null;
  DESC2: string | null;
  IsIMG1: boolean;
  IsIMG2: boolean;
  SHORT_FORM: string | null;
  QText: string;
}

interface RSItem {
  Status: boolean;
  QNUM: number;
  COMMENTS: string;
  INSTANCES: number;
  IMG1: string | null;
  IMG2: string | null;
  DESC1: string | null;
  DESC2: string | null;
  IsIMG1: boolean;
  IsIMG2: boolean;
  SHORT_FORM: string | null;
  QText: string;
}

interface DMItem {
  Status: boolean;
  QNUM: number;
  COMMENTS: string;
  INSTANCES: number;
  IMG1: string | null;
  IMG2: string | null;
  DESC1: string | null;
  DESC2: string | null;
  IsIMG1: boolean;
  IsIMG2: boolean;
  AISLE: string;
  BAY_NUM: string;
  LEVEL: number;
  SLOT_ID: number;
  Signature: string | null;
  SEVERITY: string;
  DOR: string | null;
  DORCOMPLETION: string | null;
  SHORT_FORM: string;
  QText: string;
}

interface RackingInspectionData {
  LOC: string;
  PW: string;
  WALK_STARTED_BY: string;
  LAST_UPDT_BY: string;
  LAST_UPDT_BY_USERNAME: string;
  SIGN: SignData;
  MSC: MiscData;
  LC: LCItem[];
  RS: RSItem[];
  DM: DMItem[];
}

// Define the state interface for this slice
interface SelectedRackingWalkState {
  data: RackingInspectionData | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: SelectedRackingWalkState = {
  data: null,
  loading: false,
  error: null,
};

// Create the selected racking walk slice
const selectedRackingWalkSlice = createSlice({
  name: "selectedRackingWalk",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setData: (state, action: PayloadAction<RackingInspectionData | null>) => {
      state.data = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearSelectedRackingWalk: (state) => {
      state.data = null;
      state.error = null;
      state.loading = false;
    },
  },
});

// Export actions
export const { setLoading, setData, setError, clearSelectedRackingWalk } =
  selectedRackingWalkSlice.actions;

// Export reducer
export default selectedRackingWalkSlice.reducer;
